<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import EpointCrmPanel from 'dashboard/routes/dashboard/conversation/EpointCrmPanel.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { usePanelKhach } from 'dashboard/composables/usePanelKhach';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

// BonBoZ: trang thai doc tu `usePanelKhach` — dien thoai giu rieng trong phien,
// may tinh van doc/ghi cau hinh tren server nhu cu.
const { dangMo: panelDangMo, dat: datPanel, laDienThoai } = usePanelKhach();

const activeTab = computed(() => (panelDangMo.value ? 0 : null));

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (!isSmallScreen.value || !panelDangMo.value) return;
  datPanel(false);
  // Copilot van la cau hinh dung chung cua upstream, khong doi.
  if (uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({ is_copilot_panel_open: false });
  }
};

// BonBoZ: hàng tab "Thông tin / Tạo đơn" của Pancake. Nhớ tab đang mở qua
// uiSettings để đổi hội thoại không nhảy về tab đầu.
const CAC_TAB = [{ ten: 'thong_tin' }, { ten: 'tao_don' }];

// Khoá tĩnh để linter bắt được bản dịch thiếu.
const nhanTab = computed(() => ({
  thong_tin: t('BONBOZ.SIDEBAR.TAB_THONG_TIN'),
  tao_don: t('BONBOZ.SIDEBAR.TAB_TAO_DON'),
}));
const tabDangMo = computed(
  () => uiSettings.value.bonboz_sidebar_tab || 'thong_tin'
);
const chonTab = ten => updateUISettings({ bonboz_sidebar_tab: ten });

const contactId = computed(() => props.currentChat.meta?.sender?.id);
</script>

<template>
  <!-- BonBoZ: thêm nấc 3xl để sidebar rộng thêm trên màn lớn, giữ nguyên 300/336. -->
  <div
    v-on-click-outside="[
      () => closeContactPanel(),
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[300px] md:min-w-[300px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[336px] 2xl:w-[336px] 3xl:min-w-[372px] 3xl:w-[372px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab === 0,
        'md:hidden': activeTab !== 0,
      },
    ]"
  >
    <div class="flex items-center shrink-0 border-b border-n-weak">
      <button
        v-for="tab in CAC_TAB"
        :key="tab.ten"
        type="button"
        class="bonboz-tab flex-1"
        :class="{ 'bonboz-tab-active': tabDangMo === tab.ten }"
        @click="chonTab(tab.ten)"
      >
        {{ nhanTab[tab.ten] }}
      </button>
      <!-- BonBoZ: nut dong CHI tren dien thoai. O do panel la lop phu rong
           384px, tuc phu kin man 360px - khong con "vung ngoai" nao de bam
           thoat. Rieng tab "Tao don" con khong ve nut X cua ContactPanel, nen
           truoc day phai chuyen ve tab Thong tin roi moi dong duoc. -->
      <button
        v-if="laDienThoai"
        type="button"
        class="flex justify-center items-center mx-1 rounded-lg size-9 shrink-0 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :aria-label="$t('BONBOZ.SIDEBAR.DONG')"
        @click="datPanel(false)"
      >
        <span class="size-5 i-lucide-x" aria-hidden="true" />
      </button>
    </div>
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        v-show="activeTab === 0 && tabDangMo === 'thong_tin'"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
      <EpointCrmPanel
        v-if="activeTab === 0 && tabDangMo === 'tao_don'"
        :contact-id="contactId"
        class="w-full"
      />
    </div>
  </div>
</template>
